import { Loader } from "@trackman/web-shared-components";
import { AppButton, AppInput, AppTextarea } from "components/form";
import { AppDialog, AppSectionErrorBoundary, AppSpacer } from "components/ui";
import { AppAutoComplete, AutoCompleteOptionType } from "components/ui/AppAutoComplete";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IDiagnosticsPackages } from "types/models/DiagnosticsPackages";

interface ISelectDiagnosticsPackageDiaglogProps {
    isOpened: boolean;
    onClose: () => void;
    onSuccessAfterClose: () => void;
    selectedEntries: string[];
}

export const SelectDiagnosticsPackageDiaglogDiaglog = observer(({ isOpened, onClose, onSuccessAfterClose, selectedEntries }: ISelectDiagnosticsPackageDiaglogProps) => {
    const { t } = useTranslation();
    const { diagnosticsPackagesStore, notificationStore } = useStores();

    const [selectedDiagnosticsPackageOption, setSelectedDiagnosticsPackageOption] = useState<string | null>(null);
    const [foundDiagnosticsPackage, setFoundDiagnosticsPackage] = useState<IDiagnosticsPackages | null>(null);

    const handleDiagnosticsPackageAddedSuccessfully = useCallback(() => {
        resetForm();
        onClose();
        onSuccessAfterClose();
    }, []);

    const onCancelHandler = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose]);

    const resetForm = useCallback(() => {
        setSelectedDiagnosticsPackageOption(null);
        setFoundDiagnosticsPackage(null);
    }, []);

    const onSubmitHandler = async () => {
        if (!selectedDiagnosticsPackageOption || !foundDiagnosticsPackage) {
            return;
        }

        await diagnosticsPackagesStore.patchEntriesDiagnosticsPackage(
            foundDiagnosticsPackage.id,
            selectedEntries,
            () => {
                handleDiagnosticsPackageAddedSuccessfully();
            }
        );
    };

    const isSubmitButtonDisabled = () => {
        if (!selectedDiagnosticsPackageOption || !foundDiagnosticsPackage) return true;
        if (diagnosticsPackagesStore.isFormLoading) return true;

        return false;
    };

    const onAutoCompleteChange = (newValue: string, newData?: IDiagnosticsPackages) => {
        if (!newValue || newValue === '') {
            return;
        }

        if (!newData) {
            notificationStore.addToast('Error', t('DiagnosticsPackages.Error.PackageNotFound'), 'error');
            return;
        }

        setSelectedDiagnosticsPackageOption(`${newData.name} - ${newData.packageTags}`);
        setFoundDiagnosticsPackage(newData);
    }

    const diagnosticsPackages = useMemo(() => diagnosticsPackagesStore.diagnosticsPackages, [diagnosticsPackagesStore.diagnosticsPackages]);

    type PackagetagOptingType = AutoCompleteOptionType<IDiagnosticsPackages>;

    const selectablePackageTags = useMemo<PackagetagOptingType[]>(
        () => diagnosticsPackages?.map(x => ({ inputValue: x.name, title: `${x.name} - ${x.packageTags}`, data: x })
        ),
        [diagnosticsPackages]
    );

    return (
        <AppDialog
            title={`${t('DiagnosticsPackages.Dialog.SelectPackage')}`}
            isOpened={isOpened}
            onClose={onClose}
        >
            <div className='flex-auto d-flex flex-column justify-content-between' id="modal-root">
                <AppSectionErrorBoundary>
                    <div>
                        <section className='container'>
                            <AppAutoComplete
                                value={selectedDiagnosticsPackageOption ?? ''}
                                onValueChange={onAutoCompleteChange}
                                isLoading={diagnosticsPackagesStore.isFormLoading}
                                allOptions={selectablePackageTags}
                                freeSolo={false}
                                label={`${t('DiagnosticsPackages.Column.Name')} - ${t('DiagnosticsPackages.Column.PackageTag')}`}
                            />
                            <AppSpacer space='15px' />
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('DiagnosticsPackages.Section.Form')}</span>
                            </h3>
                            <AppInput
                                label={t('DiagnosticsPackages.Column.Name')}
                                name={'name'}
                                value={foundDiagnosticsPackage?.name || ''}
                                disabled={true}
                            />
                            <AppSpacer space='15px' />
                            <AppInput
                                label={t('DiagnosticsPackages.Column.PackageTag')}
                                name={'description'}
                                value={foundDiagnosticsPackage?.packageTags[0] || ''}
                                disabled={true}
                            />
                            <AppSpacer space='15px' />
                            <AppTextarea
                                label={t('DiagnosticsPackages.Column.Description')}
                                name={'description'}
                                value={foundDiagnosticsPackage?.description ?? ''}
                                disabled={true}
                                onChange={() => { }}
                            />
                            <AppSpacer space='15px' />
                            <div>
                                <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                    <span className='mr-2'>{t('DiagnosticsPackages.Section.Info')}</span>
                                </h3>
                                <div className='d-flex flex-row my-3'>
                                    <AppInput
                                        label={t('DiagnosticsPackages.Column.CreatorId')}
                                        name={'creatorId'}
                                        value={foundDiagnosticsPackage?.createdByUserId ?? '-'}
                                        disabled={true}
                                    />
                                    <AppSpacer isHorizontal space='1rem' />
                                    <AppInput
                                        label={t('DiagnosticsPackages.Column.CreatorEmail')}
                                        name={'email'}
                                        value={foundDiagnosticsPackage?.creatorEmail ?? '-'}
                                        disabled={true}
                                    />
                                </div>
                                <div className='d-flex flex-row my-3'>
                                    <AppInput
                                        label={t('DiagnosticsPackages.Column.AddedAt')}
                                        name={'addedAtUtc'}
                                        value={foundDiagnosticsPackage?.addedAtUtc ?? '-'}
                                        disabled={true}
                                    />
                                    <AppSpacer isHorizontal space='1rem' />
                                    <AppInput
                                        label={t('DiagnosticsPackages.Column.UpdatedAt')}
                                        name={'updatedAtUtc'}
                                        value={foundDiagnosticsPackage?.updatedAtUtc ?? '-'}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </section>
                        <AppSpacer space='20px' />
                    </div>
                    <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
                        <AppButton variant='secondary' onClick={onCancelHandler} data-test='diagnostics-package-select-dialog-cancel-button'>
                            {t('cancel')}
                        </AppButton>
                        <AppSpacer space='10px' />
                        <p className="font-10 font-700 font-lato text-capitalize color-secondaryGrey mb-0">{t(`${t('DiagnosticsPackages.SelectPackage.Message', { amount: `${selectedEntries.length}` })}`)}</p>
                        <div className='d-flex'>
                            {diagnosticsPackagesStore.isFormLoading && <Loader />}
                            <AppSpacer space='10px' />
                            <AppButton
                                variant='primary'
                                disabled={isSubmitButtonDisabled()}
                                onClick={onSubmitHandler}
                                data-test='diagnostics-package-select-dialog-submit-button'
                            >
                                {t('DiagnosticsPackages.Action.AddExisting')}
                            </AppButton>
                        </div>
                    </footer>
                </AppSectionErrorBoundary>
            </div>
        </AppDialog>
    );
});